import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Phone, MessageCircle, ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import { MaskedLines, Reveal } from '../components/Motion';
import CTASection from '../components/CTASection';
import FAQ from '../components/FAQ';
import {
  CONTACT, SERVICE_CATEGORIES, WHY_CHOOSE, WHY_POINTS, PROCESS, INDUSTRIES, IMAGES,
} from '../lib/data';

/* ---------------- Hero ---------------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[100svh] flex items-end overflow-hidden bg-primary">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.printingPress}
          alt="Offset printing press producing corporate print material"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/70 to-primary/30" />
      </motion.div>

      <div className="relative max-w-[1400px] w-full mx-auto px-6 md:px-12 pb-16 md:pb-24 pt-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="overline text-accent"
        >
          Delhi-based · Serving Pan India
        </motion.p>

        <h1 className="font-display mt-6 text-white text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-7xl font-semibold tracking-tight max-w-5xl">
          <MaskedLines
            onLoad
            lines={[
              'Your Trusted Partner for',
              <>Printing, Packaging <span className="text-accent">&amp;</span></>,
              'Corporate Solutions',
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed"
        >
          From business stationery and custom packaging to corporate merchandise,
          Shiva Prints &amp; Packs delivers reliable solutions with quality,
          flexibility and nationwide service.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            href={CONTACT.tel}
            data-testid="hero-call"
            className="group inline-flex items-center gap-2 bg-white text-primary px-7 py-4 text-sm font-semibold hover:bg-accent hover:text-white transition-colors duration-300"
          >
            <Phone className="h-4 w-4" /> Call Now
          </a>
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp"
            className="group inline-flex items-center gap-2 bg-[#25D366] text-white px-7 py-4 text-sm font-semibold hover:brightness-110 transition-all duration-300"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp Us
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- Marquee ---------------- */
const words = ['OFFSET PRINTING', 'PREMIUM PACKAGING', 'CORPORATE MERCHANDISE', 'DESIGN SOLUTIONS', 'CUSTOM BOXES', 'NATIONWIDE DELIVERY'];
const BrandMarquee = () => (
  <div className="border-y border-border bg-background py-6">
    <Marquee speed={40} gradient={false} autoFill>
      {words.map((w, i) => (
        <span key={i} className="mx-8 flex items-center gap-8 font-display text-2xl md:text-3xl font-medium text-foreground/80">
          {w} <span className="h-2 w-2 rounded-full bg-accent" />
        </span>
      ))}
    </Marquee>
  </div>
);

/* ---------------- About / Manifesto ---------------- */
const About = () => (
  <section data-testid="home-about" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
      <div className="lg:col-span-7">
        <Reveal>
          <p className="overline">01 — Who we are</p>
          <h2 className="font-display mt-6 text-4xl md:text-5xl font-medium tracking-tight leading-[1.1]">
            A one-stop partner for print, packaging and physical branding.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 text-lg text-muted-foreground leading-relaxed max-w-2xl">
            With <span className="text-foreground font-medium">10+ years of industry experience</span>, Shiva Prints &amp; Packs
            helps corporate offices, businesses and startups meet their complete printing and packaging
            requirements. We combine in-house production with trusted manufacturing partners — so you get
            flexibility, reliable quality and timely delivery, all from a single point of contact.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to="/about"
            data-testid="home-about-link"
            className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold link-underline"
          >
            More about us
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Reveal delay={0.1}>
          <div className="overflow-hidden">
            <img src={IMAGES.packagingBoxes} alt="Premium custom packaging boxes" className="w-full h-[420px] object-cover hover:scale-105 transition-transform duration-700" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Services Bento ---------------- */
const spans = ['md:col-span-7', 'md:col-span-5', 'md:col-span-5', 'md:col-span-7', 'md:col-span-12'];
const ServicesBento = () => (
  <section data-testid="home-services" className="bg-muted/60">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal>
        <p className="overline">02 — What we do</p>
        <h2 className="font-display mt-6 text-4xl md:text-5xl font-medium tracking-tight max-w-3xl">Our services</h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5">
        {SERVICE_CATEGORIES.map((cat, i) => {
          const Icon = cat.icon;
          return (
            <Reveal key={cat.id} delay={i * 0.08} className={spans[i]}>
              <Link
                to="/services"
                data-testid={`home-service-${cat.id}`}
                className="group block h-full bg-card border border-border p-8 md:p-10 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-shadow duration-300"
              >
                <div className="flex items-start justify-between">
                  <span className="flex h-12 w-12 items-center justify-center bg-primary text-primary-foreground group-hover:bg-accent transition-colors duration-300">
                    <Icon className="h-6 w-6" />
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-accent" />
                </div>
                <h3 className="font-display mt-8 text-2xl md:text-3xl font-medium tracking-tight">{cat.title}</h3>
                <p className="mt-3 text-muted-foreground max-w-md">{cat.tagline}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {cat.items.slice(0, 5).map((it) => (
                    <span key={it.name} className="text-xs font-medium border border-border px-3 py-1.5 text-foreground/70">{it.name}</span>
                  ))}
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- Why Choose Us ---------------- */
const WhyChoose = () => (
  <section data-testid="home-why" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
    <Reveal>
      <p className="overline">03 — Why Shiva Prints &amp; Packs</p>
      <h2 className="font-display mt-6 text-4xl md:text-5xl font-medium tracking-tight max-w-3xl">
        Built for businesses that value reliability.
      </h2>
    </Reveal>

    <div className="mt-14 grid grid-cols-2 xl:grid-cols-4 border-t border-l border-border">
      {WHY_CHOOSE.map((s, i) => (
        <Reveal key={i} delay={i * 0.06} className="border-b border-r border-border">
          <div className="group h-full min-h-[220px] md:min-h-[260px] bg-card px-8 py-14 md:px-12 md:py-16 flex flex-col justify-center transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_20px_45px_rgb(0,0,0,0.08)]">
            <div className="flex items-center min-h-[40px] md:min-h-[44px]">
              <p className="font-display font-semibold text-primary leading-tight tracking-tight text-xl md:text-2xl xl:text-xl">{s.stat}</p>
            </div>
            <p className="mt-6 text-sm md:text-base font-normal text-muted-foreground leading-[1.6] max-w-[26ch]">{s.label}</p>
          </div>
        </Reveal>
      ))}
    </div>

    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
      {WHY_POINTS.map((p, i) => (
        <Reveal key={p.title} delay={i * 0.06} className="flex gap-5">
          <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center bg-accent/10 text-accent">
            <Check className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-display text-xl font-medium">{p.title}</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">{p.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- Process ---------------- */
const Process = () => (
  <section data-testid="home-process" className="bg-primary text-primary-foreground noise">
    <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal>
        <p className="overline">04 — How it works</p>
        <h2 className="font-display mt-6 text-4xl md:text-5xl font-medium tracking-tight max-w-3xl">Our process</h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {PROCESS.map((p, i) => (
          <Reveal key={p.step} delay={i * 0.08} className="bg-primary p-8 md:p-10">
            <span className="font-display text-accent text-5xl font-semibold">{p.step}</span>
            <h3 className="font-display mt-6 text-2xl font-medium">{p.title}</h3>
            <p className="mt-3 text-primary-foreground/70 leading-relaxed">{p.desc}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Industries ---------------- */
const Industries = () => (
  <section data-testid="home-industries" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
    <Reveal>
      <p className="overline">05 — Who we serve</p>
      <h2 className="font-display mt-6 text-4xl md:text-5xl font-medium tracking-tight max-w-3xl">Industries we serve</h2>
    </Reveal>
    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 border-t border-l border-border">
      {INDUSTRIES.map((ind, i) => {
        const Icon = ind.icon;
        return (
          <Reveal key={ind.name} delay={i * 0.05} className="group border-b border-r border-border p-8 hover:bg-primary transition-colors duration-300">
            <Icon className="h-8 w-8 text-accent" />
            <p className="mt-6 font-display text-lg font-medium group-hover:text-primary-foreground transition-colors duration-300">{ind.name}</p>
          </Reveal>
        );
      })}
    </div>
  </section>
);

export default function Home() {
  useEffect(() => {
    document.title = 'Shiva Prints & Packs | Printing & Packaging Company in Delhi, Pan India';
  }, []);

  return (
    <>
      <Hero />
      <BrandMarquee />
      <About />
      <ServicesBento />
      <WhyChoose />
      <Process />
      <Industries />
      <FAQ />
      <CTASection />
    </>
  );
}
